/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */

import supportAPI from "../_apis/supportAPI";

/**
 * Validate that review_id is a positive integer
 * @param {number|string} review_id - The review ID to validate
 * @throws {Error} If review_id is not a positive integer
 */
const validateReviewId = (review_id) => {
    const numericId = parseInt(review_id, 10);
    if (!Number.isInteger(numericId) || numericId <= 0) {
        throw new Error(`Invalid review_id: must be a positive integer, got ${review_id}`);
    }
};

/**
 * Validate that review_ticket_id is a positive integer
 * @param {number|string} review_ticket_id - The ticket ID to validate
 * @throws {Error} If review_ticket_id is not a positive integer
 */
const validateTicketId = (review_ticket_id) => {
    const numericId = parseInt(review_ticket_id, 10);
    if (!Number.isInteger(numericId) || numericId <= 0) {
        throw new Error(`Invalid review_ticket_id: must be a positive integer, got ${review_ticket_id}`);
    }
};

export const createReview = async (review) => {
    const response = await supportAPI.post('/review', JSON.stringify(review));
    if (!response.data) {
        throw new Error('Review not created.');
    }
    return response.data;
};

/**
 * Fetches a review and its associated tickets by review_id (integer primary key)
 * @param {number} review_id - The numeric review ID
 * @returns {Promise<Object>} Review object with tickets array
 */
export const fetchReview = async (review_id) => {
    validateReviewId(review_id);
    const response = await supportAPI.get(`/review/${review_id}/tickets`);
    if (!response.data) {
        throw new Error('Review not found.');
    }
    return response.data;
};

export const fetchAllReviews = async () => {
    const response = await supportAPI.get('/review');
    return response.data || [];
};

/**
 * Updates a review by review_id (integer primary key)
 * @param {number} review_id - The numeric review ID
 * @param {Object} updates - Fields to update
 */
export const updateReview = async (review_id, updates) => {
    validateReviewId(review_id);
    const response = await supportAPI.put(`/review/${review_id}`, JSON.stringify(updates));
    return response.data;
};

/**
 * Deletes a review by review_id (integer primary key)
 * @param {number} review_id - The numeric review ID
 */
export const deleteReview = async (review_id) => {
    validateReviewId(review_id);
    const response = await supportAPI.delete(`/review/${review_id}`);
    return response.data;
};

/**
 * Adds a ticket to a review
 * @param {number} review_id - The numeric review ID
 * @param {Object} ticket - Ticket data to create
 */
export const addReviewTicket = async (review_id, ticket) => {
    validateReviewId(review_id);
    const response = await supportAPI.post(`/review/${review_id}/tickets`, JSON.stringify(ticket));
    return response.data;
};

/**
 * Updates a review ticket
 * @param {number} review_id - The numeric review ID
 * @param {number} review_ticket_id - The numeric ticket ID (primary key)
 * @param {Object} updates - Fields to update
 */
export const updateReviewTicket = async (review_id, review_ticket_id, updates) => {
    validateReviewId(review_id);
    validateTicketId(review_ticket_id);
    const response = await supportAPI.put(`/review/${review_id}/tickets/${review_ticket_id}`, JSON.stringify(updates));
    return response.data;
};

/**
 * Deletes a review ticket
 * @param {number} review_id - The numeric review ID
 * @param {number} review_ticket_id - The numeric ticket ID (primary key)
 */
export const deleteReviewTicket = async (review_id, review_ticket_id) => {
    validateReviewId(review_id);
    validateTicketId(review_ticket_id);
    const response = await supportAPI.delete(`/review/${review_id}/tickets/${review_ticket_id}`);
    return response.data;
};

/**
 * Generate registration link for a review
 * @param {number} review_id - The review ID (must be positive integer)
 * @returns {string} - Full registration URL with review_id parameter
 */
export const generateRegistrationLink = (review_id) => {
    const numericId = parseInt(review_id, 10);
    if (!Number.isInteger(numericId) || numericId <= 0) {
        throw new Error(`Invalid review_id: must be a positive integer, got ${review_id}`);
    }
    return `${window.location.origin}/register?review_id=${numericId}`;
};

/**
 * Generates a registration link with the numeric review_id parameter
 * @param {number} review_id - The numeric review ID
 */
export const generateEmailTemplate = (review) => {
    const ticketCount = review.tickets?.length || 0;
    const promoText = review.promo_type === 'credit'
        ? `$${review.promo_value} in credit`
        : `${review.promo_value}% off`;

    return `Subject: Quick website review for ${review.prospect_company}

Hi ${review.prospect_name},

I took a look at ${review.prospect_url} and recorded a quick video walking through some opportunities I noticed:

🎥 Watch the review: ${review.video_url}

I identified ${ticketCount} items that could improve your site's performance, security, and user experience.

If you'd like us to handle these for you, I've already created tickets for each issue. Just register here and they'll be waiting in your dashboard:

👉 Register now: ${generateRegistrationLink(review.review_id)}

As a thank-you for watching, you'll get ${promoText} automatically applied.

Looking forward to working with you!

Best,
[Your Name]`;
};

